import { Data, Misc, ModalUtils } from '@singularsystems/neo-core';
import { Views } from '@singularsystems/neo-react';
import { NotificationDuration } from '../../../App/Models/NotificationDuration';
import { AppService, DomainTypes, Types } from '../../DomainTypes';
import PortalUser from '../../Models/PortalUser/PortalUser';
import PortalUserPagedCriteria from '../../Models/PortalUser/Lookups/PortalUserPagedCriteria';
import AuthorisationVM from '../../Services/AuthorisationVM';
import { AuthorisationTypes } from "@singularsystems/neo-authorisation";
import ContactsVM from "../Contacts/ContactsVM";

export default class PortalUsersVM extends Views.ViewModelBase {

    constructor(
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        private notifications = AppService.get(Types.Neo.UI.GlobalNotifications),
        public authorisationService = AppService.get(Types.Neo.Security.AuthorisationService),
        public apiClient = AppService.get(DomainTypes.ApiClients.PortalUser),
        public authorisationDataCache = AppService.get(AuthorisationTypes.DataCache),
        public investorApiClient = AppService.get(DomainTypes.ApiClients.Investor)) {

        super(taskRunner);
        this.makeObservable();
        this.autoDispose(this.criteria.onAnyPropertyChanged(() => this.pageManager.refreshData()))
    }

    public editingPortalUser: PortalUser | null = null;

    public criteria = new PortalUserPagedCriteria();
    public authorisationVM = this.registerViewModel(AuthorisationVM);
    public contactsVM = this.registerViewModel(ContactsVM);

    public pageManager = new Data.PageManager(this.criteria, PortalUser, this.apiClient.getPortalUsersPaged, {
        pageSize: 20,
        initialTaskRunner: this.taskRunner,
        fetchInitial: true,
        allowSort: true,
        sortBy: 'firstName',
        sortAscending: true,
    });

    public showSaveButton() {
        if (this.editingPortalUser) {
            return this.editingPortalUser.isDirty;
        };
        return false;
    }

    public async editPortalUser(portalUser: PortalUser | null) {
        if (portalUser) {
            const data = await this.taskRunner.waitForData(this.apiClient.getPortalUser(portalUser.portalUserId));
            this.editingPortalUser = PortalUser.fromJSObject<PortalUser>(data);
        }
        else {
            this.editingPortalUser = new PortalUser();
        }
    }

    public async savePortalUser() {

        this.taskRunner.run(async () => {
            if (this.editingPortalUser) {
                await this.apiClient.save(this.editingPortalUser.toJSObject());
                this.notifications.addSuccess("Save Successful", "Portal User was saved successfully.", NotificationDuration.Standard);
                this.pageManager.refreshPage();
                this.editingPortalUser = null;
            }
        });
    }

    public async setPendingDelete(portalUser: PortalUser) {
        if (portalUser.isNew) {
            this.pageManager.data.removeWithoutTracking(portalUser);
        }
        else {
            var heading = `Mark Portal User to be removed?`;
            var message = `Are you sure you want to mark '${portalUser.loginName}' to be removed when authorised?`;
            var successHeading = "Portal User Removal Pending";
            var successMessage = `${portalUser.loginName} has been marked for removal when authorised`;
            if (portalUser.isPendingDelete) {
                heading = `Stop Portal User Removal?`;
                message = `Are you sure you want to stop '${portalUser.loginName}' from being removed when authorised?`;
                successHeading = "Portal User Removal Stopped";
                successMessage = `${portalUser.loginName} will not be removed when authorised`;
            }
            if (await ModalUtils.showYesNo(heading, message) === Misc.ModalResult.Yes) {
                this.taskRunner.run(async () => {
                    await this.apiClient.setPendingDelete(portalUser.portalUserId, !portalUser.isPendingDelete);
                    this.notifications.addSuccess(successHeading, successMessage, NotificationDuration.Standard);
                    this.pageManager.refreshData();
                });
            }
        }
    }
}